import { useEffect, useState } from "react";
import { CheckCircle2, Clock, Loader2, QrCode, RefreshCw } from "lucide-react";
import { Modal } from "../shared/Modal";
import { CopyButton } from "../shared/CopyButton";
import { formatBRL } from "../../lib/format";
import type { ApiError } from "../../lib/api";
import type { CheckoutSession } from "../../types/api";
import type { CheckoutPhase } from "../../hooks/usePixCheckout";

interface Props {
  open: boolean;
  onClose: () => void;
  session: CheckoutSession | null;
  phase: CheckoutPhase;
  remainingMs: number;
  price: number;
  error: ApiError | null;
  simulating: boolean;
  onRenew: () => void;
  onSimulate: () => void;
}

const mmss = (ms: number): string => {
  const total = Math.ceil(ms / 1000);
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
};

function QrImage({ remote, local }: { remote: string; local: string }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [remote]);
  const useRemote = Boolean(remote) && !failed;
  return (
    <img
      src={useRemote ? remote : local}
      onError={() => setFailed(true)}
      alt="QR Code PIX para pagamento"
      data-testid="pix-qr-image"
      data-source={useRemote ? "gateway" : "local"}
      className="h-[240px] w-[240px] [image-rendering:pixelated]"
    />
  );
}

function Success() {
  return (
    <div data-testid="payment-success" className="modal-pop flex flex-col items-center py-8 text-center">
      <CheckCircle2 aria-hidden="true" className="h-16 w-16 text-emerald-400 motion-safe:animate-[pulse_1s_ease-in-out_1]" />
      <p className="mt-4 text-lg font-semibold text-slate-100">Pagamento Confirmado com Sucesso!</p>
      <p className="mt-1 text-sm text-slate-400">Liberando seu documento...</p>
    </div>
  );
}

function Expired({ onRenew, busy }: { onRenew: () => void; busy: boolean }) {
  return (
    <div data-testid="pix-expired" className="flex flex-col items-center py-6 text-center">
      <Clock aria-hidden="true" className="h-12 w-12 text-amber-400" />
      <p className="mt-4 text-base font-semibold text-slate-100">Chave expirada</p>
      <p className="mt-1 text-sm text-slate-400">O prazo de 15 minutos para pagamento terminou.</p>
      <button type="button" onClick={onRenew} disabled={busy} data-testid="renew-pix-button" className="btn-primary mt-6 disabled:opacity-60">
        {busy ? <Loader2 aria-hidden="true" className="h-4 w-4 motion-safe:animate-spin" /> : <RefreshCw aria-hidden="true" className="h-4 w-4" />}Gerar nova chave
      </button>
    </div>
  );
}

function Pending({ session, remainingMs, price, phase, simulating, onSimulate }: Pick<Props, "remainingMs" | "price" | "phase" | "simulating" | "onSimulate"> & { session: CheckoutSession }) {
  return (
    <div data-testid="pix-pending">
      <div className="flex items-baseline justify-between gap-4">
        <p data-testid="pix-amount" className="text-2xl font-semibold text-slate-100">{formatBRL(price)}</p>
        <p className="flex items-center gap-1.5 font-mono text-sm text-slate-300">
          <Clock aria-hidden="true" className="h-4 w-4 text-emerald-500" /><span data-testid="pix-countdown">{mmss(remainingMs)}</span>
        </p>
      </div>
      <div className="mt-5 flex justify-center rounded-md bg-white p-3">
        <QrImage remote={session.qrCodeImage} local={session.qrCodeBase64} />
      </div>
      <label htmlFor="pix-copia-cola" className="mt-5 block text-xs font-medium uppercase tracking-[0.14em] text-slate-400">PIX copia e cola</label>
      <div className="mt-2 flex flex-col gap-2 sm:flex-row">
        <input id="pix-copia-cola" readOnly value={session.copiaECola} data-testid="pix-copy-input" onFocus={(e) => e.currentTarget.select()} className="min-w-0 flex-1 truncate rounded-md border border-slate-800 bg-slate-950 px-3 py-2.5 font-mono text-xs text-slate-300 outline-none focus:border-emerald-500" />
        <CopyButton text={session.copiaECola} label="Copiar Código PIX" testId="pix-copy-button" />
      </div>
      <p data-testid="pix-waiting-indicator" className="mt-5 flex items-center gap-2 text-sm text-slate-300">
        <span className="relative flex h-2.5 w-2.5">
          <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60 motion-safe:animate-ping" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
        </span>
        {phase === "confirming" ? "Pagamento identificado, liberando documento..." : "Aguardando confirmação bancária instantânea..."}
      </p>
      {session.mode === "mock" && (
        <button type="button" onClick={onSimulate} disabled={simulating || phase === "confirming"} data-testid="simulate-payment-button" className="mt-5 w-full rounded-md border border-dashed border-slate-700 px-4 py-2 text-xs text-slate-400 transition-colors duration-200 hover:border-emerald-500/60 hover:text-emerald-400 disabled:opacity-50">
          {simulating ? "Simulando..." : "Simular Pagamento Aprovado"} <span className="text-slate-600">(homologação)</span>
        </button>
      )}
    </div>
  );
}

export function PixCheckoutModal(props: Props) {
  const { open, onClose, session, phase, error, onRenew } = props;
  const body =
    phase === "success" ? <Success /> :
    phase === "expired" || phase === "creating" ? <Expired onRenew={onRenew} busy={phase === "creating"} /> :
    session ? <Pending {...props} session={session} /> : null;
  return (
    <Modal
      open={open}
      onClose={onClose}
      testId="pix-checkout-modal"
      title="Pagamento via PIX"
      icon={<span className="flex h-11 w-11 items-center justify-center rounded-md border border-emerald-500/30 bg-emerald-500/10"><QrCode aria-hidden="true" className="h-5 w-5 text-emerald-400" /></span>}
      footer={phase === "success" ? null : <button type="button" onClick={onClose} data-testid="pix-modal-close-button" className="btn-secondary">Fechar</button>}
    >
      {body}
      {error && phase !== "success" && <p role="alert" data-testid="pix-modal-error" className="mt-4 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">{error.message}</p>}
    </Modal>
  );
}
